import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  InputGroup,
  Form,
  Pagination,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetTimelineQuery,
  useToggleTimelineStatusMutation,
  useDeleteTimelineMutation,
} from "../../../redux/api/TimelineApi/timelineApi";
import DeleteModel from "../../../Components/DeleteModel";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaHistory,
  FaCheckCircle,
  FaTimesCircle,
  FaList,
  FaTrashAlt,
} from "react-icons/fa";
import "../Services/Services.css";

const Timeline = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "timeline_date";
  const order = searchParams.get("order") || "desc";
  const limit = Number(searchParams.get("limit")) || 10;

  const [searchTerm, setSearchTerm] = useState(search);

  const [deleteTimeline] = useDeleteTimelineMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== search) {
        setSearchParams({
          page: 1,
          limit,
          search: searchTerm,
          sortBy,
          order,
        });
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm, limit, sortBy, order, search, setSearchParams]);

  const { data, isLoading } = useGetTimelineQuery({
    page,
    limit,
    search,
    sortBy,
    order,
  });

  const [toggleStatus] = useToggleTimelineStatusMutation();

  const timelineData = data?.timelineData || [];
  const totalPages = Math.ceil((data?.total || 0) / limit);

  const handleToggleStatus = async (id) => {
    try {
      await toggleStatus(id).unwrap();
      message.success("Status updated successfully!");
    } catch (error) {
      console.error("Failed to toggle status:", error);
      message.error("Failed to update status.");
    }
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTimeline(selectedItem.id).unwrap();
      setShowDeleteModal(false);
      setSelectedItem(null);
      message.success("Timeline item deleted successfully!");
    } catch (error) {
      console.error("Failed to delete timeline item:", error);
      message.error("Failed to delete timeline item.");
    }
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSelectedItem(null);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaHistory className="text-primary me-2" />
            Timeline Management
          </h2>
          <p className="text-muted mb-0">
            Manage your company's journey and milestones
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate("/admin/add-timeline")}
          className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm rounded-pill fw-semibold"
        >
          <FaPlus /> Add New Milestone
        </Button>
      </div>

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 bg-white overflow-hidden h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-primary-soft text-primary me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaList size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">{data?.total || 0}</h4>
                <div className="text-muted small fw-semibold">
                  Total Milestones
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 bg-white overflow-hidden h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-success-soft text-success me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaCheckCircle size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">
                  {
                    timelineData.filter(
                      (f) => f.status === true || f.status === 1,
                    ).length
                  }{" "}
                  Active
                </h4>
                <div className="text-muted small fw-semibold">
                  Published Milestones
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
        <Card.Header className="bg-white border-bottom p-3">
          <Row className="align-items-center">
            <Col md={3} className="d-none d-md-block">
              <h5 className="mb-0 fw-bold px-2">Milestone List</h5>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                className="w-auto cursor-pointer"
                value={limit}
                onChange={(e) =>
                  setSearchParams({
                    page: 1,
                    limit: Number(e.target.value),
                    search,
                    sortBy,
                    order,
                  })
                }
              >
                <option value={10}>Show 10</option>
                <option value={25}>Show 25</option>
                <option value={50}>Show 50</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <InputGroup className="bg-light rounded-pill border-0 overflow-hidden px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search years, titles, descriptions..."
                  className="bg-transparent border-0 shadow-none py-2"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4 border-0">S.No</th>
                <th className="border-0">Year</th>
                <th className="border-0">Title</th>
                <th className="border-0">Position</th>
                <th className="text-center border-0">Status</th>
                <th className="text-end pe-4 border-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {timelineData && timelineData.length > 0 ? (
                timelineData.map((item, i) => (
                  <tr key={item.id}>
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark">
                        {i + 1 + (page - 1) * limit}
                      </div>
                    </td>
                    <td>
                      <Badge
                        bg="primary-soft"
                        className="text-primary px-3 py-2 rounded-pill fw-bold"
                      >
                        {item.timeline_year}
                      </Badge>
                    </td>
                    <td>
                      <div className="fw-semibold text-dark">
                        {item.timeline_title || "No Title"}
                      </div>
                      <div
                        className="small text-muted text-truncate"
                        style={{ maxWidth: "250px" }}
                      >
                        {item.timeline_description}
                      </div>
                    </td>
                    <td>
                      <Badge
                        bg={item.position === "left" ? "info" : "warning"}
                        className="text-capitalize"
                      >
                        {item.position}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleToggleStatus(item.id)}
                      >
                        {item.status ? (
                          <Badge
                            bg="success-light"
                            className="text-success border-0 px-3 py-2 rounded-pill"
                          >
                            <FaCheckCircle size={10} className="me-1" /> Active
                          </Badge>
                        ) : (
                          <Badge
                            bg="danger-light"
                            className="text-danger border-0 px-3 py-2 rounded-pill"
                          >
                            <FaTimesCircle size={10} className="me-1" />{" "}
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-end pe-4">
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() =>
                            navigate(`/admin/edit-timeline/${item.id}`)
                          }
                          className="btn-action shadow-sm border"
                        >
                          <FaEdit className="text-primary" />
                        </Button>
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() => handleDelete(item)}
                          className="btn-action shadow-sm border"
                        >
                          <FaTrashAlt className="text-danger" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <div className="py-4">
                      <FaHistory size={48} className="text-light mb-3" />
                      <h5 className="text-muted fw-bold">
                        No Timeline Milestones Found
                      </h5>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {totalPages > 1 && (
        <Pagination className="justify-content-center my-4 custom-pagination">
          <Pagination.Prev
            disabled={page === 1}
            onClick={() =>
              setSearchParams({ page: page - 1, limit, search, sortBy, order })
            }
          />
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={page === i + 1}
              onClick={() =>
                setSearchParams({ page: i + 1, limit, search, sortBy, order })
              }
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page === totalPages}
            onClick={() =>
              setSearchParams({ page: page + 1, limit, search, sortBy, order })
            }
          />
        </Pagination>
      )}
      <DeleteModel
        DELETESTATE={showDeleteModal}
        ONCLICK={handleCloseModal}
        YES={handleDeleteConfirm}
        DELETETITLE="Delete Milestone"
        DESCRIPTION={`Are you sure you want to delete this milestone?`}
        loadTest="Deleting..."
      />
    </div>
  );
};

export default Timeline;
